import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaAd,
  FaLock,
  FaWallet,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaExternalLinkAlt,
  FaCopy,
  FaArrowDown,
  FaArrowUp,
  FaUserSlash,
  FaInbox,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaBan,
  FaUnlock,
  FaExclamationTriangle,
  FaUsers,
  FaCrown,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes awFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes awFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes awBar { from { width: 0; } }
@keyframes awOpen { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@keyframes awSpin { to { transform: rotate(360deg); } }
.aw-in { animation: awFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.aw-fade { animation: awFade .2s ease both; }
.aw-bar { animation: awBar .9s cubic-bezier(.2,.7,.2,1) both; }
.aw-open { animation: awOpen .3s cubic-bezier(.2,.8,.2,1) both; }
.aw-ring { animation: awSpin 18s linear infinite; }
.aw-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.aw-press:hover { transform: translateY(-1px); }
.aw-press:active { transform: scale(.96); }
.aw-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .aw-in, .aw-fade, .aw-bar, .aw-open, .aw-ring { animation: none; }
  .aw-press:hover, .aw-press:active { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const ago = (d) => {
  if (!d) return "never";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const m = Math.floor(days / 30);
  return `${m} month${m > 1 ? "s" : ""} ago`;
};
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);
const avail = (w) => num(w.wallet_balance);
const hold = (w) => num(w.hold_balance);
const total = (w) => avail(w) + hold(w);

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="aw-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Initials = ({ name, size = 36 }) => {
  const t = nm(name)
    ? nm(name)
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : null;
  return (
    <span className={`rounded-full shrink-0 flex items-center justify-center font-semibold ${t ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200" : "bg-slate-100 text-slate-400 dark:bg-slate-700"}`} style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {t || <FaUserSlash size={size * 0.4} />}
    </span>
  );
};

// Two-part bar: available (indigo) + on hold (amber)
const SplitBar = ({ a, h, max }) => (
  <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden flex">
    <div className="aw-bar h-full bg-indigo-500" style={{ width: `${(a / max) * 100}%` }} />
    <div className="aw-bar h-full bg-amber-400" style={{ width: `${(h / max) * 100}%` }} />
  </div>
);

/* ================= page ================= */

const AdsRewardWalletList = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [usersLoaded, setUsersLoaded] = useState(false);

  const [query, setQuery] = useState("");
  const [flag, setFlag] = useState("all"); // all | hold | available | empty | unknown | blocked
  const [sort, setSort] = useState({ field: "total", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(null);
  const [peek, setPeek] = useState({});
  const [checked, setChecked] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const postTo = useCallback(
    async (file, action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/${file}`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const [w, u] = await Promise.all([postTo("wallet.php", "adsWalletList"), postTo("users.php", "all-users").catch(() => null)]);
      setWallets(Array.isArray(w?.wallets) ? w.wallets : []);
      if (Array.isArray(u)) {
        const sm = {};
        u.forEach((x) => (sm[String(x.user_id)] = x.status || "Live"));
        setUserStatus(sm);
        setUsersLoaded(true);
      }
      setPeek({});
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load ads wallets" });
    }
    setLoading(false);
  }, [postTo]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const gone = useCallback((w) => !nm(w.name) || (usersLoaded && !(String(w.user_id) in userStatus)), [usersLoaded, userStatus]);
  const label = (w) => (gone(w) ? "Unknown user" : nm(w.name));
  const blocked = (w) => String(userStatus[String(w.user_id)] || "").toLowerCase() === "block";

  const loadPeek = async (user_id) => {
    if (peek[user_id] && peek[user_id] !== "error") return;
    setPeek((p) => ({ ...p, [user_id]: "loading" }));
    try {
      const res = await postTo("wallet.php", "getAdsWalletData", { user_id });
      setPeek((p) => ({ ...p, [user_id]: res?.status ? res.data : "error" }));
    } catch (e) {
      setPeek((p) => ({ ...p, [user_id]: "error" }));
    }
  };

  const toggleExpand = (id) => {
    if (expanded === id) setExpanded(null);
    else {
      setExpanded(id);
      loadPeek(id);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    const status = type === "block" ? "Block" : type === "unblock" ? "Live" : "Delete";
    setWorking(true);
    let ok = 0;
    for (const w of list) {
      try {
        const res = await postTo("users.php", "block-user", { user_id: w.user_id, status });
        if (res && res.status === false) throw new Error("Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setDeleteText("");
    setChecked([]);
    setExpanded(null);
    const verb = status === "Block" ? "blocked" : status === "Live" ? "unblocked" : "removed";
    if (ok) toast.current?.show({ severity: "success", summary: "Done", detail: `${ok} account${ok > 1 ? "s" : ""} ${verb}` });
    if (ok < list.length) toast.current?.show({ severity: "error", summary: "Error", detail: `${list.length - ok} could not be ${verb}` });
    fetchWallets();
  };

  /* ----- derived ----- */

  const q = query.trim();
  const maxTotal = useMemo(() => Math.max(1, ...wallets.map(total)), [wallets]);

  const stats = useMemo(() => {
    const a = wallets.reduce((s, w) => s + avail(w), 0);
    const h = wallets.reduce((s, w) => s + hold(w), 0);
    const unknown = wallets.filter(gone);
    return {
      avail: a,
      hold: h,
      all: a + h,
      users: wallets.length,
      withHold: wallets.filter((w) => hold(w) > 0).length,
      unknownCount: unknown.length,
      unknownMoney: unknown.reduce((s, w) => s + total(w), 0),
    };
  }, [wallets, gone]);

  const top = useMemo(() => [...wallets].sort((x, y) => total(y) - total(x)).slice(0, 5), [wallets]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let l = wallets.filter((w) => {
      if (flag === "hold" && hold(w) <= 0) return false;
      if (flag === "available" && avail(w) <= 0) return false;
      if (flag === "empty" && total(w) > 0) return false;
      if (flag === "unknown" && !gone(w)) return false;
      if (flag === "blocked" && !blocked(w)) return false;
      if (!ql) return true;
      return [label(w), w.user_id].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    l = [...l].sort((a, b) => {
      let x;
      let y;
      if (sort.field === "total") [x, y] = [total(a), total(b)];
      else if (sort.field === "avail") [x, y] = [avail(a), avail(b)];
      else if (sort.field === "hold") [x, y] = [hold(a), hold(b)];
      else if (sort.field === "last_update") [x, y] = [parseAny(a.last_update)?.getTime() || 0, parseAny(b.last_update)?.getTime() || 0];
      else [x, y] = [String(gone(a) ? "~" : nm(a.name)).toLowerCase(), String(gone(b) ? "~" : nm(b.name)).toLowerCase()];
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, q, flag, sort, gone, userStatus]);

  useEffect(() => setPage(1), [q, flag, sort, pageSize]);
  useEffect(() => {
    const valid = new Set(wallets.map((w) => w.user_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [wallets]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const checkedList = wallets.filter((w) => checked.includes(w.user_id));

  const toggleSort = (field) => setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        setExpanded(null);
        setConfirm((c) => (working ? c : null));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working]);

  const copyText = (text, lbl = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: lbl, detail: String(text), life: 1500 });
  };

  const exportCsv = () => {
    const head = ["User ID", "Name", "Wallet Balance", "Hold Balance", "Total", "Last Update"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((w) => [w.user_id, label(w), avail(w), hold(w), total(w), w.last_update].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-ads-wallets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button type="button" onClick={() => toggleSort(field)} className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white">
          {children}
          <Icon size={10} className={active ? "text-indigo-600" : "text-slate-300"} />
        </button>
      </th>
    );
  };

  /* ----- peek ----- */

  const renderPeek = (w) => {
    const data = peek[w.user_id];
    if (!data || data === "loading")
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      );
    if (data === "error")
      return (
        <p className="text-sm text-rose-600">
          Could not load this account.{" "}
          <button type="button" onClick={() => loadPeek(w.user_id)} className="underline">
            Try again
          </button>
        </p>
      );
    const txns = data.transactions || [];
    const earned = txns.filter((t) => t.type === "credit").reduce((s, t) => s + num(t.coin), 0);
    const spent = txns.filter((t) => t.type !== "credit").reduce((s, t) => s + num(t.coin), 0);
    const holds = txns.filter((t) => String(t.status).toLowerCase() === "hold");
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 text-sm space-y-2">
          <p className="text-xs font-semibold text-slate-500">Contact</p>
          <p className="text-slate-700 dark:text-slate-200 break-all">{data.user?.email_id || "-"}</p>
          <p className="text-slate-700 dark:text-slate-200">{data.user?.phone_num || "-"}</p>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => navigate(`/user-profile/${w.user_id}`)} className="text-xs font-medium text-indigo-700 dark:text-indigo-400 hover:underline inline-flex items-center gap-1">
              Profile <FaExternalLinkAlt size={9} />
            </button>
            <button type="button" onClick={() => navigate(`/ads-reward-wallet-account/${w.user_id}`)} className="text-xs font-medium text-indigo-700 dark:text-indigo-400 hover:underline inline-flex items-center gap-1">
              Full account <FaExternalLinkAlt size={9} />
            </button>
          </div>
        </div>
        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-semibold text-slate-500">Money flow</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2.5">
              <p className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                <FaArrowDown size={9} /> In
              </p>
              <p className="font-bold text-emerald-700 tabular-nums">{money(earned)}</p>
            </div>
            <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 p-2.5">
              <p className="text-[11px] text-rose-700 inline-flex items-center gap-1">
                <FaArrowUp size={9} /> Out
              </p>
              <p className="font-bold text-rose-700 tabular-nums">{money(spent)}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
            <FaLock size={10} /> {holds.length} item{holds.length === 1 ? "" : "s"} on hold ({money(holds.reduce((s, t) => s + num(t.coin), 0))})
          </p>
        </div>
        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500">Latest activity</p>
            <span className="text-[11px] text-slate-400">{txns.length} total</span>
          </div>
          <ul className="mt-2 space-y-1.5">
            {txns.slice(0, 5).map((t) => {
              const cr = t.type === "credit";
              const onHold = String(t.status).toLowerCase() === "hold";
              return (
                <li key={t.tnx_id} className="flex items-center gap-2 text-xs">
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${onHold ? "bg-amber-100 text-amber-700" : cr ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {onHold ? <FaLock size={9} /> : cr ? <FaArrowDown size={9} /> : <FaArrowUp size={9} />}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-slate-600 dark:text-slate-300" title={t.description}>
                    {t.description || t.type}
                  </span>
                  <b className={`tabular-nums ${onHold ? "text-amber-700" : cr ? "text-emerald-700" : "text-rose-700"}`}>
                    {cr ? "+" : "-"}
                    {money(t.coin)}
                  </b>
                </li>
              );
            })}
            {!txns.length && <li className="text-xs text-slate-400">No activity yet</li>}
          </ul>
        </div>
      </div>
    );
  };

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="aw-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Finance</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Ads Reward Wallet Management</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Rewards users earn from ads. Available money can be used; money on hold is still being checked.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="aw-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchWallets} disabled={loading} className="aw-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="aw-in relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-700 via-violet-800 to-slate-900 text-white p-6 xl:col-span-2">
          <div className="pointer-events-none absolute -right-16 -bottom-16 w-72 h-72 rounded-full border-[18px] border-white/5 aw-ring" />
          <div className="pointer-events-none absolute right-10 -top-10 w-40 h-40 rounded-full bg-fuchsia-500/20 blur-3xl" />
          <div className="relative flex flex-wrap items-center gap-6">
            {/* donut */}
            <div className="relative w-36 h-36 shrink-0">
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: `conic-gradient(#818cf8 0 ${stats.all ? (stats.avail / stats.all) * 360 : 0}deg, #fbbf24 0 360deg)` }}
              />
              <div className="absolute inset-3 rounded-full bg-indigo-900 flex flex-col items-center justify-center text-center">
                <FaAd className="text-indigo-300" />
                <p className="text-lg font-black tabular-nums leading-tight">{money(stats.all)}</p>
                <p className="text-[10px] text-white/60">total rewards</p>
              </div>
            </div>
            <div className="flex-1 min-w-[220px] grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setFlag(flag === "available" ? "all" : "available")} className={`aw-press text-left rounded-2xl p-4 border ${flag === "available" ? "bg-white text-indigo-900 border-white" : "bg-white/10 border-white/10"}`}>
                <p className={`text-xs inline-flex items-center gap-1.5 ${flag === "available" ? "text-indigo-700" : "text-indigo-200"}`}>
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" /> Available
                </p>
                <p className="text-2xl font-black tabular-nums">{money(stats.avail)}</p>
                <p className={`text-[11px] ${flag === "available" ? "text-indigo-700/70" : "text-white/60"}`}>ready to use</p>
              </button>
              <button type="button" onClick={() => setFlag(flag === "hold" ? "all" : "hold")} className={`aw-press text-left rounded-2xl p-4 border ${flag === "hold" ? "bg-white text-amber-900 border-white" : "bg-white/10 border-white/10"}`}>
                <p className={`text-xs inline-flex items-center gap-1.5 ${flag === "hold" ? "text-amber-700" : "text-amber-200"}`}>
                  <FaLock size={10} /> On hold
                </p>
                <p className="text-2xl font-black tabular-nums">{money(stats.hold)}</p>
                <p className={`text-[11px] ${flag === "hold" ? "text-amber-700/70" : "text-white/60"}`}>{stats.withHold} users waiting</p>
              </button>
              <div className="col-span-2 flex items-center gap-2 text-xs text-white/70">
                <FaUsers size={11} /> {stats.users} users have an ads wallet
              </div>
            </div>
          </div>
        </div>

        <div className={`${CARD} aw-in p-5`} style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-2">
            <FaCrown className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top earners</h2>
          </div>
          <ul className="mt-3 space-y-2">
            {top.map((w, i) => (
              <li key={w.user_id}>
                <button type="button" onClick={() => navigate(`/ads-reward-wallet-account/${w.user_id}`)} className="aw-press w-full flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/40 text-left">
                  <span className={`w-5 text-xs font-bold text-center ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
                  <Initials name={gone(w) ? null : w.name} size={30} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${gone(w) ? "text-slate-400 italic" : "text-slate-900 dark:text-white"}`}>{label(w)}</p>
                    <div className="mt-1">
                      <SplitBar a={avail(w)} h={hold(w)} max={maxTotal} />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">{money(total(w))}</span>
                </button>
              </li>
            ))}
            {!top.length && <li className="text-sm text-slate-500">No ads wallets yet</li>}
          </ul>
          {stats.unknownCount > 0 && (
            <>
              <button type="button" onClick={() => setFlag("unknown")} className="aw-press mt-4 w-full flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 p-3 text-left text-xs text-rose-900 dark:text-rose-200">
                <FaUserSlash className="mt-0.5 shrink-0" />
                <span>
                  <b>{stats.unknownCount}</b> wallet{stats.unknownCount > 1 ? "s belong" : " belongs"} to users who no longer exist ({money(stats.unknownMoney)}). Tap to review. Removing them needs a small server update.
                </span>
              </button>

            </>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className={`${CARD} aw-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "160ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or user ID (type 'unknown' for missing users)" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <div className="inline-flex flex-wrap p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "all", label: "All" },
            { id: "hold", label: `On hold (${stats.withHold})` },
            { id: "available", label: "Has money" },
            { id: "empty", label: "Empty" },
            { id: "unknown", label: `Unknown (${stats.unknownCount})` },
            { id: "blocked", label: "Blocked" },
          ].map((f) => (
            <button key={f.id} type="button" onClick={() => setFlag(f.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${flag === f.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk */}
      {checked.length > 0 && (
        <div className="aw-fade mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2.5 text-sm">
          <span className="font-semibold text-indigo-900 dark:text-indigo-100 mr-2">{checked.length} selected</span>
          <button type="button" onClick={() => setConfirm({ type: "block", list: checkedList.filter((w) => !gone(w) && !blocked(w)) })} disabled={!checkedList.some((w) => !gone(w) && !blocked(w))} className="aw-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-40">
            <FaBan size={11} /> Block
          </button>
          <button type="button" onClick={() => setConfirm({ type: "unblock", list: checkedList.filter(blocked) })} disabled={!checkedList.some(blocked)} className="aw-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40">
            <FaUnlock size={11} /> Unblock
          </button>

          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className={`${CARD} aw-in mt-4 overflow-hidden`} style={{ animationDelay: "200ms" }}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            <b>{filtered.length}</b> wallet{filtered.length === 1 ? "" : "s"} {q && <>for "<b>{q}</b>"</>}
          </p>
          <p className="text-xs text-slate-400 inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" /> available
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> on hold
            </span>
            Click a row to peek inside
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="pl-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={pageRows.length > 0 && pageRows.every((w) => checked.includes(w.user_id))}
                    onChange={() => {
                      const ids = pageRows.map((w) => w.user_id);
                      const all = ids.every((id) => checked.includes(id));
                      setChecked((c) => (all ? c.filter((id) => !ids.includes(id)) : Array.from(new Set([...c, ...ids]))));
                    }}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"
                    aria-label="Select page"
                  />
                </th>
                <th className="py-3 w-6" />
                <SortTh field="name">User</SortTh>
                <SortTh field="avail" right>Wallet Balance</SortTh>
                <SortTh field="hold" right>Hold Balance</SortTh>
                <SortTh field="total">Total</SortTh>
                <SortTh field="last_update">Last Update</SortTh>
                <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && !wallets.length &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td colSpan={8} className="px-4 py-2">
                      <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    </td>
                  </tr>
                ))}
              {!loading && !pageRows.length && (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center">
                    <FaInbox className="mx-auto text-slate-300" size={32} />
                    <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">{wallets.length ? "No wallet matches these filters" : "No ads wallets yet"}</p>
                  </td>
                </tr>
              )}
              {pageRows.map((w) => {
                const open = expanded === w.user_id;
                const d = parseAny(w.last_update);
                return (
                  <React.Fragment key={w.user_id}>
                    <tr onClick={() => toggleExpand(w.user_id)} className={`cursor-pointer border-b border-slate-100 dark:border-slate-700 transition-colors ${open ? "bg-indigo-50/60 dark:bg-indigo-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                      <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={checked.includes(w.user_id)} onChange={() => setChecked((c) => (c.includes(w.user_id) ? c.filter((x) => x !== w.user_id) : [...c, w.user_id]))} className="w-4 h-4 accent-indigo-600 cursor-pointer" aria-label="Select wallet" />
                      </td>
                      <td className="py-3 text-slate-400">{open ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <Initials name={gone(w) ? null : w.name} />
                          <div className="min-w-0">
                            <p className={`font-medium truncate flex items-center gap-1.5 ${gone(w) ? "text-slate-400 italic" : "text-slate-900 dark:text-white"}`}>
                              <Hi text={label(w)} q={q} />
                              {gone(w) && <span className="not-italic text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">NOT FOUND</span>}
                              {blocked(w) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">BLOCKED</span>}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyText(w.user_id, "User ID copied");
                              }}
                              className="text-xs text-slate-500 tabular-nums hover:text-slate-800 inline-flex items-center gap-1"
                            >
                              <Hi text={w.user_id} q={q} /> <FaCopy size={8} />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className={`px-3 py-3 text-right font-bold tabular-nums ${avail(w) > 0 ? "text-indigo-700 dark:text-indigo-300" : "text-slate-400"}`}>{money(avail(w))}</td>
                      <td className="px-3 py-3 text-right">
                        {hold(w) > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 tabular-nums">
                            <FaLock size={9} /> {money(hold(w))}
                          </span>
                        ) : (
                          <span className="text-slate-400 tabular-nums">{money(0)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 min-w-[160px]">
                        <p className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200 mb-1">{money(total(w))}</p>
                        <SplitBar a={avail(w)} h={hold(w)} max={maxTotal} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{ago(d)}</div>
                        <div className="text-xs text-slate-500">
                          {fmtDate(d)}, {fmtTime(d)}
                        </div>
                      </td>
                      <td className="px-3 py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1.5">
                          {!gone(w) && (
                            <button type="button" onClick={() => navigate(`/ads-reward-wallet-account/${w.user_id}`)} className="aw-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold whitespace-nowrap">
                              <FaWallet size={9} /> View Account
                            </button>
                          )}
                          {!gone(w) && (
                            <button
                              type="button"
                              onClick={() => setConfirm({ type: blocked(w) ? "unblock" : "block", list: [w] })}
                              aria-label={blocked(w) ? "Unblock user" : "Block user"}
                              title={blocked(w) ? "Unblock user" : "Block user"}
                              className={`aw-press w-8 h-8 rounded-lg flex items-center justify-center ${blocked(w) ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                            >
                              {blocked(w) ? <FaUnlock size={11} /> : <FaBan size={11} />}
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-indigo-50/40 dark:bg-indigo-900/10 border-b border-slate-100 dark:border-slate-700">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="aw-open">{renderPeek(w)}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              Rows per page
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${INPUT} h-8 px-2`}>
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="aw-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                  <FaChevronLeft size={10} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="aw-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                  <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm */}
      {confirm && confirm.list.length > 0 && (() => {
        const list = confirm.list;
        const isDelete = confirm.type === "delete";
        const real = list.filter((w) => !gone(w));
        const sum = list.reduce((s, w) => s + total(w), 0);
        const ready = !isDelete || deleteText.trim().toUpperCase() === "DELETE";
        return (
          <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
            <div className="aw-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
            <div className="aw-in relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
              <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "unblock" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                {isDelete ? <FaExclamationTriangle size={18} /> : confirm.type === "unblock" ? <FaUnlock size={16} /> : <FaBan size={16} />}
              </span>
              <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
                {isDelete ? (list.length === 1 ? (gone(list[0]) ? "Remove this unknown wallet?" : `Delete ${label(list[0])} completely?`) : `Delete ${list.length} accounts?`) : confirm.type === "block" ? `Block ${list.length === 1 ? label(list[0]) : `${list.length} users`}?` : `Unblock ${list.length === 1 ? label(list[0]) : `${list.length} users`}?`}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {isDelete
                  ? real.length
                    ? `${real.length} real account${real.length > 1 ? "s" : ""} will be deleted completely, with their wallets and all data. This cannot be undone.`
                    : "Leftover data of users who no longer exist will be cleaned up. This cannot be undone."
                  : confirm.type === "block"
                  ? "They cannot use the app and get an 'Account Suspended' notification."
                  : "They can use the app again and get an 'Account Activated' notification."}
              </p>
              <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
                {list.slice(0, 30).map((w) => (
                  <div key={w.user_id} className="flex items-center gap-3 p-2.5">
                    <Initials name={gone(w) ? null : w.name} size={28} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${gone(w) ? "text-slate-400 italic" : "text-slate-900 dark:text-white"}`}>{label(w)}</p>
                      <p className="text-xs text-slate-500 tabular-nums">{w.user_id}</p>
                    </div>
                    <span className="text-sm font-bold tabular-nums text-indigo-700">{money(total(w))}</span>
                  </div>
                ))}
              </div>
              {isDelete && sum > 0 && (
                <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" /> {money(sum)} in rewards (available and on hold) will be wiped out.
                </p>
              )}
              {isDelete && (
                <div className="mt-4">
                  <label htmlFor="aw-del" className="text-sm text-slate-700 dark:text-slate-200">
                    Type <b>DELETE</b> to confirm
                  </label>
                  <input id="aw-del" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className={`${INPUT} w-full h-10 px-3 mt-1.5`} />
                </div>
              )}
              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => setConfirm(null)} disabled={working} className="aw-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runConfirm}
                  disabled={working || !ready}
                  className={`aw-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${confirm.type === "unblock" ? "bg-emerald-600 hover:bg-emerald-700" : confirm.type === "block" ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-600 hover:bg-rose-700"}`}
                >
                  {working && <FaSyncAlt className="animate-spin" size={12} />}
                  {working ? "Please wait..." : isDelete ? "Delete forever" : confirm.type === "block" ? "Yes, block" : "Yes, unblock"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdsRewardWalletList;